//src/components/layout/AppSidebar.tsx
import {
  AlertTriangle,
  BarChart3,
  Bell,
  Building2,
  ClipboardCheck,
  FileText,
  FolderOpen,
  GraduationCap,
  LayoutDashboard,
  ListTodo,
  Settings,
  Shield,
  UserCog,
  BriefcaseBusiness,
  SlidersHorizontal,
  Users,
  Send,
  ShieldCheck,
  ListTree,
  BrainCircuit,
} from "lucide-react";
import { useLocation } from "react-router-dom";

import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/contexts/AuthContext";
import { useGroSettings } from "@/hooks/useGroSettings";
import { PERMISSIONS } from "@/lib/permissions";
import { NormexLogo } from "./NormexLogo";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";

interface MenuItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  permissions?: string[];
}

interface MenuGroup {
  label: string;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    label: "Principal",
    items: [
      {
        title: "Dashboard",
        url: "/",
        icon: LayoutDashboard,
        permissions: [PERMISSIONS.DASHBOARD_READ],
      },
    ],
  },
  {
    label: "Cadastros",
    items: [
      {
        title: "Empresa",
        url: "/cadastros/empresa",
        icon: Building2,
        permissions: [PERMISSIONS.TENANT_READ, PERMISSIONS.PLATFORM_TENANTS_READ],
      },
      {
        title: "Unidades",
        url: "/cadastros/unidades",
        icon: Building2,
        permissions: [PERMISSIONS.UNITS_READ],
      },
      {
        title: "Departamentos",
        url: "/cadastros/departamentos",
        icon: FolderOpen,
        permissions: [PERMISSIONS.DEPARTMENTS_READ],
      },
      {
        title: "Cargos",
        url: "/cadastros/cargos",
        icon: BriefcaseBusiness,
        permissions: [PERMISSIONS.JOB_POSITIONS_READ],
      },
      {
        title: "Grupos de Acesso",
        url: "/cadastros/grupos",
        icon: UserCog,
        permissions: [PERMISSIONS.GROUPS_READ],
      },
      {
        title: "Usuários",
        url: "/cadastros/usuarios",
        icon: Users,
        permissions: [PERMISSIONS.USERS_READ],
      },
    ],
  },
  {
    label: "Estrutura GRO",
    items: [
      {
        title: "Configurações",
        url: "/gro/settings",
        icon: SlidersHorizontal,
        permissions: [PERMISSIONS.GRO_SETTINGS_READ],
      },
      {
        title: "Riscos",
        url: "/cadastros/riscos",
        icon: AlertTriangle,
        permissions: [PERMISSIONS.RISKS_READ],
      },
      {
        title: "Dimensões",
        url: "/gro/dimensions",
        icon: ListTree,
        permissions: [PERMISSIONS.QUESTIONNAIRES_READ],
      },
      {
        title: "Dimensões x Riscos",
        url: "/gro/dimension-risk-rules",
        icon: ShieldCheck,
        permissions: [PERMISSIONS.QUESTIONNAIRES_READ],
      },      
      {
        title: "Grupos de Avaliação",
        url: "/gro/worker-groups",
        icon: Users,
        permissions: [PERMISSIONS.WORKER_GROUPS_READ],
      },
      {
        title: "Questionários",
        url: "/gro/questionnaires",
        icon: FileText,
        permissions: [PERMISSIONS.QUESTIONNAIRES_READ],
      },   
      {
        title: "Campanhas",
        url: "/gro/campaigns",
        icon: Send,
        permissions: [PERMISSIONS.SURVEY_CAMPAIGNS_READ],
      },
      {
        title: "Fila de Moderação",
        url: "/gro/risk-suggestions",
        icon: ShieldCheck,
        permissions: [PERMISSIONS.SURVEY_RESULTS_READ, PERMISSIONS.RISK_INVENTORY_WRITE],
      },
      {
        title: "Inventários de Riscos",
        url: "/gro/risk-inventories",
        icon: AlertTriangle,
        permissions: [PERMISSIONS.RISK_INVENTORY_READ],
      },
      {
        title: "Planos de Ação",
        url: "/gro/action-plans",
        icon: ClipboardCheck,
        permissions: [PERMISSIONS.ACTION_PLANS_READ],
      },
      {
        title: "Execução de Planos",
        url: "/gro/action-plans/execution",
        icon: ListTodo,
        permissions: [PERMISSIONS.ACTION_PLANS_READ],
      },
    ],
  },
  {
    label: "Operações",
    items: [
      {
        title: "Documentos",
        url: "/documentos",
        icon: FileText,
        permissions: [PERMISSIONS.DOCUMENTS_READ],
      },
      {
        title: "Treinamentos",
        url: "/treinamentos",
        icon: GraduationCap,
        permissions: [PERMISSIONS.TRAININGS_READ],
      },
    ],
  },
  {
    label: "Análises",
    items: [
      {
        title: "Relatórios",
        url: "/relatorios",
        icon: BarChart3,
        permissions: [PERMISSIONS.REPORTS_READ],
      },
      {
        title: "Histórico de AI",
        url: "/relatorios/historico-ai",
        icon: BrainCircuit,
        permissions: [PERMISSIONS.REPORTS_READ],
      },
      {
        title: "Notificações",
        url: "/notificacoes",
        icon: Bell,
        permissions: [PERMISSIONS.NOTIFICATIONS_READ],
      },
    ],
  },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const { user } = useAuth();
  const groSettingsQ = useGroSettings();
  const showModerationQueue = groSettingsQ.data?.risk_suggestion_mode !== "auto_approve";

  function hasPermission(required?: string[]) {
    if (!required || required.length === 0) return true;
    if (!user) return false;
    return required.some((permission) => user.permissions.includes(permission));
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4">
        <a href="/" className="block">
          <NormexLogo collapsed={collapsed} />
        </a>
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        {menuGroups.map((group) => {
          const visibleItems = group.items.filter((item) => {
            if (item.url === "/gro/risk-suggestions" && !showModerationQueue) {
              return false;
            }
            return hasPermission(item.permissions);
          });

          if (visibleItems.length === 0) return null;

          return (
            <SidebarGroup key={group.label}>
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>

              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleItems.map((item) => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton
                        asChild
                        isActive={
                          item.url === "/gro/action-plans"
                            ? location.pathname === item.url
                            : location.pathname === item.url || location.pathname.startsWith(item.url + "/")
                        }
                        tooltip={item.title}
                      >
                        <NavLink
                          to={item.url}
                          end={item.url === "/" || item.url === "/gro/action-plans"}
                          className="hover:bg-sidebar-accent/50"
                          activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter className="p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Configurações">
              <NavLink
                to="/configuracoes"
                className="hover:bg-sidebar-accent/50"
                activeClassName="bg-sidebar-accent text-sidebar-accent-foreground"
              >
                <Settings className="h-4 w-4" />
                {!collapsed && <span>Configurações</span>}
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
